import { useState, useCallback } from 'react';
import { api_call } from '../helpers/untils';

/**
 * useStripeSubscription
 *
 * Encapsulates the full 3-step Stripe subscription flow:
 *   1. POST /subscriptions/setup  → get clientSecret + customerId
 *   2. stripe.confirmSetup()      → confirm card in the browser
 *   3. POST /subscriptions/create → create the subscription server-side
 */
export function useStripeSubscription() {
  const [state, setState] = useState({
    step: 'idle',   // idle | setup | confirming | subscribing | success | error
    error: null,
    stripePublishableKey: '',
    clientSecret: null,
    customerId: null,
    subscriptionId: null,
  });

  // Step 1 — fetch a SetupIntent so <Elements> can render the card form
  const initSetup = useCallback(async ({ email, name }) => {
    setState(s => ({ ...s, step: 'setup', error: null }));
    try {
      const res = await api_call(`/api/payment/stripe/subscriptions/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });

      if (res.ok) {
        const data = await res.json();

        setState(s => ({
          ...s,
          stripePublishableKey: data.STRIPE_PUBLISHABLE_KEY,
          clientSecret: data.clientSecret,
          customerId: data.customerId,
        }));
        return data;
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setState(s => ({ ...s, step: 'error', error: err.message }));
      throw err;
    }
  }, []);

  // Step 2+3 — confirm the SetupIntent, then create the subscription
  const confirmAndSubscribe = useCallback(async ({ stripe, elements, priceId }) => {
    setState(s => ({ ...s, step: 'confirming', error: null }));
    try {
      // Confirm the SetupIntent — this tokenises the card
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscribe/callback`,
        },
        redirect: 'if_required',
      });

      if (setupError) throw new Error(setupError.message);

      const paymentMethodId = setupIntent.payment_method;

      setState(s => ({ ...s, step: 'subscribing' }));

      // Create the subscription using the confirmed payment method
      const res = await api_call(`/api/payment/stripe/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: state.customerId,
          paymentMethodId,
          priceId,
        }),
      });
      if (!res.ok) throw new Error(data.error);
      const data = await res.json();

      // Handle 3D Secure on the first invoice (rare but possible)
      if (data.clientSecret && data.status === 'incomplete') {
        const { error: confirmError } = await stripe.confirmCardPayment(
          data.clientSecret
        );
        if (confirmError) throw new Error(confirmError.message);
      }

      setState(s => ({
        ...s,
        step: 'success',
        subscriptionId: data.subscriptionId,
      }));
      return data;
    } catch (err) {
      setState(s => ({ ...s, step: 'error', error: err.message }));
      throw err;
    }
  }, [state.customerId]);

  const reset = useCallback(() => {
    setState({ step: 'idle', error: null, clientSecret: null, customerId: null, subscriptionId: null });
  }, []);

  return { ...state, initSetup, confirmAndSubscribe, reset };
}
